import { ClickAwayListener, Collapse, MenuItem, MenuList, Paper, type SxProps, type Theme, useTheme } from '@mui/material';
import * as React from 'react';
import { useState } from 'react';

import ArrowDown from '@/assets/icons/arrow-down-green.svg';

import Button from '../button';

export interface DropdownOption {
    value?: string | number;
    label: string;
    icon?: React.ReactNode;
    link?: string;
    onClick?: () => void;
    closeAfterClick?: boolean;
}

export type DropdownOptions = DropdownOption[];

interface DropdownProps {
    testId?: string;
    children?: React.ReactNode;
    options: DropdownOptions;
    triggerSx?: SxProps<Theme>;
    paperSx?: SxProps<Theme>;
    side?: 'left' | 'right';
    mobileTitle?: string;
    onClose?: () => void;
}

export const Dropdown = ({ testId = '', children, options, triggerSx, paperSx, side = 'right', mobileTitle, onClose }: DropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const anchorRef = React.useRef<HTMLButtonElement>(null);

    const theme = useTheme();

    const handleToggle: React.MouseEventHandler<HTMLButtonElement> = e => {
        e.stopPropagation();
        setIsOpen(prev => {
            !prev && onClose?.();
            return !prev;
        });
    };

    const handleClose = (event?: Event | React.SyntheticEvent) => {
        if (event && anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        setIsOpen(false);
    };

    const handleListKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setIsOpen(false);
        } else if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };

    const handleOnClick = React.useCallback(
        ({ link, onClick, closeAfterClick = true }: DropdownOption) =>
            () => {
                if (link) {
                    if (link.startsWith('tel:')) {
                        window.location.href = link;
                    } else {
                        window.open(link, '_blank');
                    }
                }

                onClick?.();
                closeAfterClick && handleClose();
            },
        []
    );

    const menuTransitionProps = {
        in: isOpen,
        timeout: {
            enter: 250,
            exit: 200
        }
    };

    return (
        <div
            data-testid={testId || 'base-dropdown'}
            className={`relative ${isOpen ? 'z-[4] md:rounded-[24px] md:rounded-b-[0] md:bg-white md:shadow-dropdown' : 'z-[2]'}`}
        >
            <Button
                ref={anchorRef}
                variant="contained"
                onClick={handleToggle}
                testId="button-dropdown-toggle"
                sx={{
                    borderRadius: '24px',
                    padding: '5px 5.5px',
                    bgcolor: 'white',
                    color: 'primary.800',
                    gap: '10px',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textTransform: 'unset',
                    textDecoration: 'none',
                    boxShadow: isOpen ? 'none' : '0px 4px 22.1px 0px #00000017',
                    fontSize: '14px',
                    [theme.breakpoints.up('md')]: {
                        borderRadius: isOpen ? '100px' : '100px',
                        padding: '8px 16px',
                        width: '100%',
                        fontSize: '18px'
                    },
                    position: 'relative',
                    zIndex: 100,
                    width: 'max-content',
                    minWidth: 'unset',
                    border: 'none',

                    '&:hover': {
                        boxShadow: '0px 4px 9.6px 0px #00000029',
                        bgcolor: '#fff',
                        border: 'none'
                    },
                    ...triggerSx
                }}
            >
                <div data-testid="dropdown-toggle-content" className="flex w-full items-center justify-between">
                    {children}
                    <img src={ArrowDown} alt="" className={`${isOpen ? 'rotate-180' : ''} inline-block w-3 duration-[0.25s] md:w-4`} />
                </div>
            </Button>
            <ClickAwayListener onClickAway={handleClose}>
                <Paper
                    data-testid="dropdown-content-container"
                    sx={{
                        position: 'absolute',
                        zIndex: 99,
                        right: side === 'right' ? 0 : 'unset',
                        width: 'max-content',
                        height: 0,
                        ...paperSx,
                        top: '120%',
                        [theme.breakpoints.up('md')]: {
                            position: 'relative',
                            top: 0,
                            width: '100%'
                        }
                    }}
                >
                    <Collapse
                        {...menuTransitionProps}
                        orientation="vertical"
                        data-testid="dropdown-content-collapse"
                        sx={{
                            width: '100%',
                            boxShadow: '0px 15px 13.1px 2px #00000017',
                            borderRadius: '20px',
                            position: 'relative',
                            zIndex: 100,
                            overflow: 'hidden',
                            [theme.breakpoints.up('md')]: {
                                borderRadius: '0 0 25px 25px'
                            }
                        }}
                    >
                        <MenuList
                            autoFocusItem={isOpen}
                            aria-labelledby="composition-button"
                            sx={{
                                width: '100%',
                                bgcolor: 'white',
                                position: 'relative',
                                zIndex: 100,
                                padding: '0px !important',
                                height: isOpen ? 'auto' : '0px'
                            }}
                            onKeyDown={handleListKeyDown}
                            data-testid="dropdown-content-menu-list"
                        >
                            {mobileTitle && (
                                <h6 data-testid="dropdown-content-mobile-title" className="block px-5 pb-1.5 pt-3.5 font-dmSansMedium font-medium text-grey-700 md:hidden">
                                    {mobileTitle}
                                </h6>
                            )}

                            {options?.map((item, idx) => (
                                <MenuItem
                                    onClick={handleOnClick(item)}
                                    key={item.value + idx.toString()}
                                    data-testid={`dropdown-content-menu-item-${idx}`}
                                    sx={{
                                        display: 'flex',
                                        fontWeight: 500,
                                        color: 'primary.800',
                                        gap: '10px',
                                        padding: '15px 25px 15px 20px',
                                        alignItems: 'center',
                                        minHeight: 'max-content',
                                        fontFamily: 'DM Sans',
                                        width: '100%',
                                        [theme.breakpoints.up('md')]: {
                                            padding: '12px 14px',
                                            fontSize: '18px'
                                        },
                                        ':hover': {
                                            bgcolor: 'primary.50'
                                        }
                                    }}
                                >
                                    {item?.icon}
                                    {item.label}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Collapse>
                </Paper>
            </ClickAwayListener>
            <div className={`fixed left-0 top-0 h-screen w-screen bg-[rgba(0,0,0,0.3)] ${isOpen ? 'block md:hidden' : 'hidden'} z-3`}></div>
        </div>
    );
};

export default Dropdown;
